import type { IStepperConfig, IStepperItemBuild, IStepperItemConfig } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import removeClass from '../../general/class/removeClass';

import * as exElement from '../../utils/element';
import * as exLink from '../../utils/link';

// ----------------------------------------------------------------------------

type IStepperItemResolvedConfig = IStepperConfig & IStepperItemConfig;

// ----------------------------------------------------------------------------

function data(element: HTMLElement, config: IStepperConfig = {}): IStepperItemBuild[] {
    // Default Config
    const defConfig: IStepperConfig = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const items: IStepperItemBuild[] = [];
    const dataItems = defConfig.data ?? [];

    // Foreach Data
    for (let i = 0; i < dataItems.length; i++) {
        const itemConfig = dataItems[i];

        // Default Config Item
        const defConfigItem: IStepperItemResolvedConfig = {
            indicator: String(i + 1),
        };

        // Merge
        merge(defConfigItem, itemConfig);

        // --------------------------------------------------------------------

        // Create element (item)
        const stepperItem = add({
            merge: {
                ...defConfig,
                ...defConfigItem,
                index: i,
            },
        });
        element.appendChild(stepperItem.element);

        // Items Push
        items.push(stepperItem);
    }

    // ------------------------------------------------------------------------

    // Return
    return items;
}

function add(config: IStepperItemResolvedConfig = {}): IStepperItemBuild {
    // Default Config
    const defConfig: IStepperItemResolvedConfig = {
        item: {
            class: {
                new: 'stepper-item',
            },
        },
        body: {
            class: {
                new: 'stepper-body',
            },
        },
        content: {
            class: {
                new: 'stepper-content',
            },
        },
        indicatorElement: {
            tag: 'span',
            class: {
                new: 'stepper-indicator',
            },
        },
        titleElement: {
            tag: 'span',
            class: {
                new: 'stepper-title',
            },
        },
        descriptionElement: {
            tag: 'small',
            class: {
                new: 'stepper-description',
            },
        },
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const builds = {
        element: {},
    } as IStepperItemBuild;

    // ------------------------------------------------------------------------

    // Create element (item)
    const elItem = exElement.create({
        merge: defConfig.item,
    });
    builds.element = elItem;

    // ------------------------------------------------------------------------

    // Create body
    const body = createBody(defConfig);
    elItem.appendChild(body.element);
    builds.body = body.element;
    builds.control = body.control;
    builds.indicator = body.indicator;
    builds.content = body.content;
    builds.title = body.title;
    builds.description = body.description;

    // ------------------------------------------------------------------------

    // Settings
    setState(builds, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return builds;
}

function createBody(config: IStepperItemResolvedConfig = {}): IStepperItemBuild {
    //
    const elements = {} as IStepperItemBuild;

    // Create control
    let control: HTMLElement;

    if (!isEmptyObject(config.link) || config.href != null) {
        const link = exLink.create({
            merge: config.link,
            href: config.href ?? config.link?.href,
            class: {
                new: 'stepper-link',
            },
        });
        control = link.element;
        elements.link = link.element;
    } else {
        control = exElement.create({
            merge: config.body,
        });
    }

    elements.control = control;

    // Create indicator
    const indicator = exElement.create({
        merge: config.indicatorElement,
        html: config.indicator,
    });
    control.appendChild(indicator);
    elements.indicator = indicator;

    // Create content
    const content = exElement.create({
        merge: config.content,
    });
    control.appendChild(content);
    elements.content = content;

    // Create title
    const title = exElement.create({
        merge: config.titleElement,
        html: config.title ?? config.label ?? '',
    });
    content.appendChild(title);
    elements.title = title;

    // Create description
    if (config.description != null || config.message != null || config.html != null) {
        const description = exElement.create({
            merge: config.descriptionElement,
            html: config.description ?? config.message ?? config.html,
        });
        content.appendChild(description);
        elements.description = description;
    }

    // Attach control when wrapper is needed
    if (control.classList.contains('stepper-link')) {
        const wrapper = exElement.create({
            merge: config.body,
        });
        wrapper.appendChild(control);
        elements.element = wrapper;
    } else {
        elements.element = control;
    }

    // Return
    return elements;
}

function setState(elements: IStepperItemBuild, config: IStepperItemResolvedConfig = {}): void {
    // If using active
    if (config.active === true || config.current === true) {
        addClass(elements.element, 'active');
        elements.element.setAttribute('aria-current', 'step');
    }

    // If using completed
    if (config.completed === true || config.done === true) {
        addClass(elements.element, 'completed');
    }

    // If using disabled
    if (config.disabled === true) {
        addClass(elements.element, 'disabled');

        if (elements.control instanceof HTMLAnchorElement) {
            elements.control.setAttribute('aria-disabled', 'true');
            elements.control.tabIndex = -1;
        }
    }
}

function getActiveIndex(element: HTMLElement, items: IStepperItemBuild[] = []): number {
    if (items.length === 0) {
        const elItems = element.querySelectorAll('.stepper-item');

        for (let i = 0; i < elItems.length; i++) {
            if (elItems[i].classList.contains('active')) {
                return i;
            }
        }

        return -1;
    }

    for (let i = 0; i < items.length; i++) {
        if (items[i].element.classList.contains('active')) {
            return i;
        }
    }

    return -1;
}

function activate(element: HTMLElement, index: number, items: IStepperItemBuild[] = []): IStepperItemBuild | undefined {
    const nextItem = items[index];

    if (nextItem == null || nextItem.element.classList.contains('disabled')) {
        return undefined;
    }

    const activeIndex = getActiveIndex(element, items);
    const currentItem = items[activeIndex];

    if (currentItem != null) {
        removeClass(currentItem.element, 'active');
        currentItem.element.removeAttribute('aria-current');

        if (activeIndex < index) {
            addClass(currentItem.element, 'completed');
        }
    }

    addClass(nextItem.element, 'active');
    nextItem.element.setAttribute('aria-current', 'step');

    return nextItem;
}

function previous(element: HTMLElement, items: IStepperItemBuild[] = []): IStepperItemBuild | undefined {
    if (items.length === 0) {
        return undefined;
    }

    const activeIndex = getActiveIndex(element, items);
    const startIndex = activeIndex >= 0 ? activeIndex - 1 : items.length - 1;

    for (let i = startIndex; i >= 0; i--) {
        const item = items[i];

        if (item.element.classList.contains('disabled')) {
            continue;
        }

        const currentItem = activeIndex >= 0 ? items[activeIndex] : undefined;

        if (currentItem != null) {
            removeClass(currentItem.element, 'active completed');
            currentItem.element.removeAttribute('aria-current');
        }

        removeClass(item.element, 'completed');
        addClass(item.element, 'active');
        item.element.setAttribute('aria-current', 'step');

        return item;
    }

    return undefined;
}

function next(element: HTMLElement, items: IStepperItemBuild[] = []): IStepperItemBuild | undefined {
    if (items.length === 0) {
        return undefined;
    }

    const activeIndex = getActiveIndex(element, items);
    const startIndex = activeIndex >= 0 ? activeIndex + 1 : 0;

    for (let i = startIndex; i < items.length; i++) {
        const item = items[i];

        if (!item.element.classList.contains('disabled')) {
            return activate(element, i, items);
        }
    }

    return undefined;
}

// ----------------------------------------------------------------------------

export { activate, add, createBody, data, next, previous, setState };
